import { requestJson, resolveUrl } from './json-form.js';
import { announce, focusFirstInvalid, setBusy } from './ui.js';

function setFormBusy(form, busy) {
    const surface = form.closest('[data-us-workflow-surface]') ?? form;
    setBusy(surface, busy);

    for (const button of form.querySelectorAll('button[type="submit"], input[type="submit"]')) {
        button.disabled = busy;
    }
}

function showError(form, error) {
    const fallback = globalThis.UnionStackI18n.text('Common.MessageRequestFailed');
    const message = globalThis.UnionStackI18n.localMessage(error) || fallback;
    const feedback = form.querySelector('[data-us-form-feedback]');

    if (feedback) {
        feedback.textContent = message;
    }
}

function clearError(form) {
    const feedback = form.querySelector('[data-us-form-feedback]');
    if (feedback) {
        feedback.textContent = '';
    }
}

function formValue(form, name) {
    const control = form.elements.namedItem(name);
    return control instanceof RadioNodeList ? control.value : control?.value || null;
}

async function createCase(form) {
    form.classList.add('was-validated');
    if (!form.checkValidity()) {
        focusFirstInvalid(form);
        return;
    }

    clearError(form);
    setFormBusy(form, true);

    try {
        const result = await requestJson(form.action, {
            method: form.getAttribute('method') || 'POST',
            body: JSON.stringify({
                idempotencyKey: formValue(form, 'idempotencyKey'),
                positionId: formValue(form, 'positionName'),
            }),
        });
        const caseId = result?.caseId ?? result?.CaseId;
        const destination = form.dataset.usRedirectTemplate?.replace('{caseId}', encodeURIComponent(caseId));

        if (!caseId || !destination) {
            throw globalThis.UnionStackI18n.error("Common.ClientTheCaseWasCreatedButTheNextStepCouldNotBeOpenedcca2ea");
        }

        window.location.assign(resolveUrl(destination));
    } catch (error) {
        showError(form, error);
    } finally {
        setFormBusy(form, false);
    }
}

function assignNested(target, segments, value) {
    let destination = target;
    for (let index = 0; index < segments.length - 1; index += 1) {
        destination[segments[index]] = destination[segments[index]] ?? {};
        destination = destination[segments[index]];
    }
    destination[segments.at(-1)] = value;
}

function serializeExtras(form) {
    const caseExtras = [];
    const pattern = /^\[caseExtras\]\[(\d+)\]((?:\[[^\]]+\])+)$/;

    for (const control of form.elements) {
        if (!(control instanceof HTMLInputElement
            || control instanceof HTMLSelectElement
            || control instanceof HTMLTextAreaElement)
            || !control.name
            || control.disabled) {
            continue;
        }

        const match = control.name.match(pattern);
        if (!match || (control instanceof HTMLInputElement && control.type === 'radio' && !control.checked)) {
            continue;
        }

        const index = Number(match[1]);
        const segments = Array.from(match[2].matchAll(/\[([^\]]+)\]/g), item => item[1]);
        const value = control.matches('textarea.quillEditor') && !new DOMParser().parseFromString(control.value, 'text/html').body.textContent.trim()
            ? null : control instanceof HTMLInputElement && control.type === 'tel' && !/\d/.test(control.value)
                ? null : control instanceof HTMLInputElement && control.type === 'checkbox'
            ? (control.checked ? 'true' : 'false')
            : control instanceof HTMLInputElement && control.type === 'datetime-local'
                ? (control.value ? window.UnionStackTime.inputUtc(control) : null)
                : control.matches('.maskNumberDecimal,.maskCurrency') ? control.value.replaceAll(',', '') : control.value;

        caseExtras[index] = caseExtras[index] ?? {};
        assignNested(caseExtras[index], segments, value);
    }

    const values = caseExtras.filter(Boolean);
    for (const item of values) {
        item.rowVersion = Number(item.rowVersion || 0);
        if (item.valuePerson && (item.value || !item.valuePerson.contact?.email?.trim())) delete item.valuePerson;
    }
    return { caseExtras: values };
}

function wizardSections(wizard) {
    return Array.from(wizard.querySelectorAll(':scope > [data-us-case-step]'));
}

function showStep(wizard, step, moveFocus = true) {
    const sections = wizardSections(wizard);
    const nextSection = sections.find(section => Number(section.dataset.usCaseStep) === step);
    if (!nextSection) {
        return;
    }

    for (const section of sections) {
        const active = section === nextSection;
        section.hidden = !active;
        section.classList.toggle('d-none', !active);
        section.setAttribute('aria-hidden', String(!active));
    }

    wizard.dataset.usCurrentStep = String(step);
    const heading = nextSection.querySelector('h2');
    if (moveFocus) {
        announce(nextSection.dataset.usStepAnnouncement || heading?.textContent?.trim() || '');
        heading?.focus({ preventScroll: true });
        nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}

async function saveExtras(form) {
    form.classList.add('was-validated');
    if (!form.checkValidity()) {
        focusFirstInvalid(form);
        return;
    }

    const wizard = form.closest('[data-us-case-wizard]');
    const section = form.closest('[data-us-case-step]');
    if (!wizard || !section) {
        return;
    }

    clearError(form);
    setFormBusy(form, true);

    try {
        const saved = await requestJson(form.action, {
            method: form.getAttribute('method') || 'PUT',
            headers: { 'If-Match': `"${wizard.dataset.usRowVersion}"` },
            body: JSON.stringify({ ...serializeExtras(form), rowVersion: Number(wizard.dataset.usRowVersion) }),
        });
        wizard.dataset.usRowVersion = String(saved.rowVersion);
        if (Array.isArray(saved.extras)) {
            for (const property of wizard.querySelectorAll('input[name$="[propertyId]"]')) {
                const item = saved.extras.find(value => value.propertyId === property.value);
                const version = property.form?.elements.namedItem(property.name.replace('[propertyId]', '[rowVersion]'));
                if (version) version.value = String(item?.rowVersion ?? '');
            }
        }

        const nextStep = Number(section.dataset.usCaseStep) + 1;
        const stepCount = Number(wizard.dataset.usStepCount);
        if (nextStep < stepCount) {
            showStep(wizard, nextStep);
            return;
        }

        await publishDraft(wizard);
        window.location.assign(resolveUrl(wizard.dataset.usCompletedUrl));
    } catch (error) {
        showError(form, error);
    } finally {
        setFormBusy(form, false);
    }
}

async function publishDraft(wizard) {
    const version = Number(wizard.dataset.usRowVersion);
    await requestJson(wizard.dataset.usPublishUrl, {
        method: 'PUT',
        headers: { 'If-Match': `"${version}"` },
        body: JSON.stringify({ rowVersion: version }),
    });
}

async function populatePerson(form, select) {
    const selectedId = select.value;
    const api = select.dataset.ajaxUrl || select.getAttribute('data-ajax--url');
    if (!selectedId || !/^\/api\/People\/[0-9a-f-]{36}\/Names$/i.test(api || '')) {
        return;
    }

    if (!form.elements.namedItem(select.name.replace('[value]', '[valuePerson][firstName]'))) return;
    try {
        const person = await requestJson(api.replace('/Names', `/${encodeURIComponent(selectedId)}`));
        const values = {
            [select.name.replace('[value]', '[valuePerson][firstName]')]: person?.firstName ?? person?.FirstName ?? '',
            [select.name.replace('[value]', '[valuePerson][lastName]')]: person?.lastName ?? person?.LastName ?? '',
            [select.name.replace('[value]', '[valuePerson][contact][email]')]: person?.contact?.email ?? person?.Contact?.Email ?? '',
        };

        for (const [name, value] of Object.entries(values)) {
            const control = form.elements.namedItem(name);
            if (control instanceof HTMLInputElement) {
                control.value = value;
            }
        }
    } catch (error) {
        showError(form, error);
    }
}

function initializeCaseCreation(root = document) {
    for (const form of root.querySelectorAll('form[data-us-case-create]')) {
        if (form.dataset.usEnhanced === 'true') {
            continue;
        }
        form.dataset.usEnhanced = 'true';
        form.addEventListener('submit', event => {
            event.preventDefault();
            void createCase(form);
        });
    }

    for (const wizard of root.querySelectorAll('[data-us-case-wizard]')) {
        if (wizard.dataset.usEnhanced === 'true') {
            continue;
        }
        wizard.dataset.usEnhanced = 'true';
        showStep(wizard, Number(wizard.dataset.usCurrentStep || 0), false);

        wizard.addEventListener('click', event => {
            const back = event.target instanceof Element
                ? event.target.closest('[data-us-case-back]')
                : null;
            if (back) {
                showStep(wizard, Number(back.closest('[data-us-case-step]').dataset.usCaseStep) - 1);
            }
        });

        for (const form of wizard.querySelectorAll('form[data-us-case-submit]')) {
            form.addEventListener('submit', async event => {
                event.preventDefault();
                clearError(form);
                setFormBusy(form, true);
                try {
                    await publishDraft(wizard);
                    window.location.assign(resolveUrl(wizard.dataset.usCompletedUrl));
                } catch (error) {
                    showError(form, error);
                } finally {
                    setFormBusy(form, false);
                }
            });
        }

        for (const form of wizard.querySelectorAll('form[data-us-case-extra-form]')) {
            form.addEventListener('submit', event => {
                event.preventDefault();
                void saveExtras(form);
            });

            for (const select of form.querySelectorAll('select[data-ajax--url]')) {
                if (select.getAttribute('data-ajax--url')?.startsWith('/api/People/')) {
                    select.addEventListener('change', () => void populatePerson(form, select));
                }
            }
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeCaseCreation(), { once: true });
} else {
    initializeCaseCreation();
}

export { initializeCaseCreation, serializeExtras, showStep };
